export type CustomerFulfillment = "delivery" | "pickup";
export type CustomerOrderStage = "accepted" | "kitchen" | "packing" | "courier" | "ready" | "completed";
export type CustomerPaymentStatus = "not_required" | "pending" | "paid" | "failed";

export interface CustomerProfile {
  name: string;
  firstName: string;
  phone: string;
  email: string;
  tier: string;
  bonusBalance: number;
  favoriteNote: string;
  avatar: string;
  memberSince: string;
}

export interface CustomerAddress {
  id: string;
  title: string;
  address: string;
  detail: string;
  eta: string;
  primary: boolean;
}

export interface CustomerPaymentMethod {
  id: string;
  label: string;
  detail: string;
  primary: boolean;
}

export interface CustomerPreferences {
  removeOnionByDefault: boolean;
  saucesOnSide: boolean;
  callInsteadOfIntercom: boolean;
  halalSeparatePack: boolean;
  noCutleryByDefault: boolean;
}

export interface CustomerOrderItemSnapshot {
  dishId: string;
  name: string;
  qty: number;
  unitPrice: number;
  totalPrice: number;
  removedIngredientIds: string[];
  removedIngredientLabels: string[];
  addOnIds: string[];
  addOnLabels: string[];
}

export interface CustomerOrderRecord {
  id: string;
  status?: string;
  createdAt: string;
  fulfillment: CustomerFulfillment;
  timeLabel: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  addressTitle: string;
  address: string;
  addressDetail: string;
  note: string;
  paymentMethodId: string;
  paymentLabel: string;
  paymentProvider?: string;
  paymentStatus?: CustomerPaymentStatus;
  paymentInvoiceId?: number | null;
  paymentPaidAt?: string;
  guests: number;
  needsCutlery: boolean;
  subtotal: number;
  delivery: number;
  total: number;
  bonusAccrued: number;
  items: CustomerOrderItemSnapshot[];
}

export interface CustomerOrderStep {
  id: string;
  label: string;
  complete: boolean;
  active: boolean;
}

export interface CustomerResolvedOrder extends CustomerOrderRecord {
  stage: CustomerOrderStage;
  status: string;
  shortStatus: string;
  eta: string;
  lead: string;
  isActive: boolean;
  steps: CustomerOrderStep[];
}

export interface CustomerQuickAction {
  id: string;
  label: string;
  detail: string;
  to: string;
}

export interface CustomerState {
  profile: CustomerProfile;
  addresses: CustomerAddress[];
  paymentMethods: CustomerPaymentMethod[];
  preferences: CustomerPreferences;
  orders: CustomerOrderRecord[];
}

const now = Date.now();

export const customerQuickActions: CustomerQuickAction[] = [
  { id: "menu", label: "Меню", detail: "собрать новый заказ", to: "/app/menu" },
  { id: "cart", label: "Корзина", detail: "проверить стол и оплату", to: "/app/cart" },
  { id: "orders", label: "Заказы", detail: "трек и история", to: "/app/orders" },
  { id: "profile", label: "Профиль", detail: "адреса и настройки", to: "/app/profile" }
];

export const customerInitialState: CustomerState = {
  profile: {
    name: "Малика Юлдашева",
    firstName: "Малика",
    phone: "+7 (999) 345-88-99",
    email: "malika@izumrud-client.ru",
    tier: "Изумрудный статус",
    bonusBalance: 1280,
    favoriteNote: "Шаурма без лука, extra sauce отдельно",
    avatar: "МЮ",
    memberSince: "с июня 2023"
  },
  addresses: [
    {
      id: "home",
      title: "Дом",
      address: "Матвеевская ул., 8, корпус 1, подъезд 2",
      detail: "домофон 27, звонить за 5 минут",
      eta: "25-35 минут",
      primary: true
    },
    {
      id: "office",
      title: "Офис",
      address: "Мичуринский проспект, 31, этаж 4",
      detail: "ресепшен А, заказ на имя Малика",
      eta: "35-45 минут",
      primary: false
    }
  ],
  paymentMethods: [
    {
      id: "card-main",
      label: "Онлайн оплата",
      detail: "основная карта",
      primary: true
    }
  ],
  preferences: {
    removeOnionByDefault: true,
    saucesOnSide: true,
    callInsteadOfIntercom: true,
    halalSeparatePack: true,
    noCutleryByDefault: false
  },
  orders: [
    {
      id: "IZ-4821",
      createdAt: new Date(now - 31 * 60 * 1000).toISOString(),
      fulfillment: "delivery",
      timeLabel: "как можно скорее",
      customerName: "Малика Юлдашева",
      customerPhone: "+7 (999) 345-88-99",
      customerEmail: "malika@izumrud-client.ru",
      addressTitle: "Дом",
      address: "Матвеевская ул., 8, корпус 1, подъезд 2",
      addressDetail: "домофон 27, звонить за 5 минут",
      note: "Шаурма без лука, соус отдельно",
      paymentMethodId: "card-main",
      paymentLabel: "Visa •• 1842",
      guests: 2,
      needsCutlery: true,
      subtotal: 960,
      delivery: 180,
      total: 1140,
      bonusAccrued: 57,
      items: [
        {
          dishId: "combo-plov",
          name: "Комбо-плов",
          qty: 1,
          unitPrice: 450,
          totalPrice: 450,
          removedIngredientIds: [],
          removedIngredientLabels: [],
          addOnIds: [],
          addOnLabels: []
        },
        {
          dishId: "shaurma-kuritsa",
          name: "Шаурма в лепешке с курицей",
          qty: 1,
          unitPrice: 350,
          totalPrice: 350,
          removedIngredientIds: ["onion"],
          removedIngredientLabels: ["Лук"],
          addOnIds: ["extra-sauce"],
          addOnLabels: ["Соус"]
        },
        {
          dishId: "shurpa",
          name: "Шурпа",
          qty: 1,
          unitPrice: 160,
          totalPrice: 160,
          removedIngredientIds: [],
          removedIngredientLabels: [],
          addOnIds: [],
          addOnLabels: []
        }
      ]
    },
    {
      id: "IZ-4708",
      createdAt: new Date(now - 3 * 24 * 60 * 60 * 1000).toISOString(),
      fulfillment: "delivery",
      timeLabel: "к 19:10",
      customerName: "Малика Юлдашева",
      customerPhone: "+7 (999) 345-88-99",
      customerEmail: "malika@izumrud-client.ru",
      addressTitle: "Дом",
      address: "Матвеевская ул., 8, корпус 1, подъезд 2",
      addressDetail: "домофон 27, звонить за 5 минут",
      note: "Не делать остро",
      paymentMethodId: "card-main",
      paymentLabel: "Visa •• 1842",
      guests: 3,
      needsCutlery: true,
      subtotal: 770,
      delivery: 180,
      total: 950,
      bonusAccrued: 48,
      items: [
        {
          dishId: "plov-samarkandskiy",
          name: "Плов Самаркандский",
          qty: 1,
          unitPrice: 350,
          totalPrice: 350,
          removedIngredientIds: [],
          removedIngredientLabels: [],
          addOnIds: [],
          addOnLabels: []
        },
        {
          dishId: "shurpa",
          name: "Шурпа",
          qty: 1,
          unitPrice: 300,
          totalPrice: 300,
          removedIngredientIds: [],
          removedIngredientLabels: [],
          addOnIds: [],
          addOnLabels: []
        },
        {
          dishId: "shaurma-kuritsa",
          name: "Шаурма в лепешке с курицей",
          qty: 1,
          unitPrice: 300,
          totalPrice: 300,
          removedIngredientIds: ["onion"],
          removedIngredientLabels: ["Лук"],
          addOnIds: [],
          addOnLabels: []
        }
      ]
    },
    {
      id: "IZ-4634",
      createdAt: new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString(),
      fulfillment: "delivery",
      timeLabel: "как можно скорее",
      customerName: "Малика Юлдашева",
      customerPhone: "+7 (999) 345-88-99",
      customerEmail: "malika@izumrud-client.ru",
      addressTitle: "Офис",
      address: "Мичуринский проспект, 31, этаж 4",
      addressDetail: "ресепшен А, заказ на имя Малика",
      note: "Привезти к встрече в переговорную",
      paymentMethodId: "sbp",
      paymentLabel: "СБП",
      guests: 4,
      needsCutlery: true,
      subtotal: 1240,
      delivery: 180,
      total: 1420,
      bonusAccrued: 71,
      items: [
        {
          dishId: "kazan-kebab-govyadina",
          name: "Казан кебаб из говядины",
          qty: 1,
          unitPrice: 500,
          totalPrice: 500,
          removedIngredientIds: [],
          removedIngredientLabels: [],
          addOnIds: [],
          addOnLabels: []
        },
        {
          dishId: "manty-uygurskie",
          name: "Манты уйгурские",
          qty: 1,
          unitPrice: 350,
          totalPrice: 350,
          removedIngredientIds: [],
          removedIngredientLabels: [],
          addOnIds: [],
          addOnLabels: []
        },
        {
          dishId: "mastava",
          name: "Мастава",
          qty: 1,
          unitPrice: 390,
          totalPrice: 390,
          removedIngredientIds: [],
          removedIngredientLabels: [],
          addOnIds: [],
          addOnLabels: []
        }
      ]
    },
    {
      id: "IZ-4519",
      createdAt: new Date(now - 25 * 24 * 60 * 60 * 1000).toISOString(),
      fulfillment: "pickup",
      timeLabel: "к 18:40",
      customerName: "Малика Юлдашева",
      customerPhone: "+7 (999) 345-88-99",
      customerEmail: "malika@izumrud-client.ru",
      addressTitle: "Самовывоз",
      address: "Матвеевская ул., 8, Москва",
      addressDetail: "Чайхана Изумруд",
      note: "Позвонить, когда заказ будет на стойке",
      paymentMethodId: "cash",
      paymentLabel: "Наличными",
      guests: 2,
      needsCutlery: false,
      subtotal: 780,
      delivery: 0,
      total: 780,
      bonusAccrued: 39,
      items: [
        {
          dishId: "combo-plov",
          name: "Комбо-плов",
          qty: 1,
          unitPrice: 450,
          totalPrice: 450,
          removedIngredientIds: [],
          removedIngredientLabels: [],
          addOnIds: [],
          addOnLabels: []
        },
        {
          dishId: "uygurskiy-lagman",
          name: "Уйгурский лагман",
          qty: 1,
          unitPrice: 330,
          totalPrice: 330,
          removedIngredientIds: [],
          removedIngredientLabels: [],
          addOnIds: [],
          addOnLabels: []
        }
      ]
    }
  ]
};

function getElapsedMinutes(createdAt: string, currentTime: number) {
  return Math.max(0, Math.floor((currentTime - new Date(createdAt).getTime()) / 60000));
}

function getStageIndex(order: CustomerOrderRecord, currentTime: number) {
  const elapsed = getElapsedMinutes(order.createdAt, currentTime);

  if (order.fulfillment === "pickup") {
    if (elapsed < 4) {
      return 0;
    }

    if (elapsed < 16) {
      return 1;
    }

    if (elapsed < 28) {
      return 2;
    }

    return 3;
  }

  if (elapsed < 4) {
    return 0;
  }

  if (elapsed < 16) {
    return 1;
  }

  if (elapsed < 28) {
    return 2;
  }

  if (elapsed < 44) {
    return 3;
  }

  return 4;
}

function getPickupSteps(stageIndex: number): CustomerOrderStep[] {
  const labels = [
    { id: "accepted", label: "Принят" },
    { id: "kitchen", label: "Кухня" },
    { id: "ready", label: "Готов" },
    { id: "completed", label: "Выдан" }
  ];

  return labels.map((step, index) => ({
    ...step,
    complete: index <= stageIndex,
    active: index === stageIndex && stageIndex < labels.length - 1
  }));
}

function getDeliverySteps(stageIndex: number): CustomerOrderStep[] {
  const labels = [
    { id: "accepted", label: "Принят" },
    { id: "kitchen", label: "Кухня" },
    { id: "packing", label: "Сборка" },
    { id: "courier", label: "Курьер" },
    { id: "completed", label: "У двери" }
  ];

  return labels.map((step, index) => ({
    ...step,
    complete: index <= stageIndex,
    active: index === stageIndex && stageIndex < labels.length - 1
  }));
}

export function formatCustomerCurrency(value: number) {
  return `${new Intl.NumberFormat("ru-RU").format(value)} ₽`;
}

export function formatCustomerOrderDate(value: string) {
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long"
  }).format(new Date(value));
}

export function getPrimaryAddress(addresses: CustomerAddress[]) {
  return addresses.find((item) => item.primary) ?? addresses[0];
}

export function getPrimaryPaymentMethod(methods: CustomerPaymentMethod[]) {
  return methods.find((item) => item.primary) ?? methods[0];
}

export function getOrderItemsCount(order: CustomerOrderRecord | CustomerResolvedOrder) {
  return order.items.reduce((sum, item) => sum + item.qty, 0);
}

export function canCustomerSelfManageOrder(
  order: Pick<CustomerOrderRecord, "status" | "paymentStatus">
) {
  return (
    order.status !== "cancelled" &&
    order.status !== "completed" &&
    (order.paymentStatus === "pending" || order.paymentStatus === "failed")
  );
}

export function resolveCustomerOrder(order: CustomerOrderRecord, currentTime: number): CustomerResolvedOrder {
  if (order.status === "cancelled") {
    return {
      ...order,
      stage: "completed",
      status: "Заказ отменен",
      shortStatus: "Отменен",
      eta: "Отмена подтверждена",
      lead: "Архив",
      isActive: false,
      steps: order.fulfillment === "pickup" ? getPickupSteps(0) : getDeliverySteps(0)
    };
  }

  if (order.paymentStatus === "pending") {
    return {
      ...order,
      stage: "accepted",
      status: "Ожидаем оплату",
      shortStatus: "Оплата",
      eta: "Переход к оплате",
      lead: "Robokassa",
      isActive: true,
      steps: order.fulfillment === "pickup" ? getPickupSteps(0) : getDeliverySteps(0)
    };
  }

  if (order.paymentStatus === "failed") {
    return {
      ...order,
      stage: "completed",
      status: "Оплата не завершена",
      shortStatus: "Не оплачено",
      eta: "Можно повторить",
      lead: "Оплата",
      isActive: false,
      steps: order.fulfillment === "pickup" ? getPickupSteps(0) : getDeliverySteps(0)
    };
  }

  const stageIndex = getStageIndex(order, currentTime);

  if (order.fulfillment === "pickup") {
    const stages: Array<{ stage: CustomerOrderStage; status: string; shortStatus: string; eta: string; lead: string }> =
      [
        {
          stage: "accepted",
          status: "Принят и пошел в работу",
          shortStatus: "Принят",
          eta: "18 минут",
          lead: "Самовывоз"
        },
        {
          stage: "kitchen",
          status: "На кухне и в сборке",
          shortStatus: "Кухня",
          eta: "10 минут",
          lead: "Готовим"
        },
        {
          stage: "ready",
          status: "Готов к выдаче",
          shortStatus: "Готов",
          eta: "На стойке",
          lead: "Самовывоз"
        },
        {
          stage: "completed",
          status: "Выдан гостю",
          shortStatus: "Выдан",
          eta: "Завершен",
          lead: "Архив"
        }
      ];

    const meta = stages[stageIndex];

    return {
      ...order,
      stage: meta.stage,
      status: meta.status,
      shortStatus: meta.shortStatus,
      eta: meta.eta,
      lead: meta.lead,
      isActive: meta.stage !== "completed",
      steps: getPickupSteps(stageIndex)
    };
  }

  const stages: Array<{ stage: CustomerOrderStage; status: string; shortStatus: string; eta: string; lead: string }> = [
    {
      stage: "accepted",
      status: "Принят и подтвержден менеджером",
      shortStatus: "Принят",
      eta: "35 минут",
      lead: "Подтвержден"
    },
    {
      stage: "kitchen",
      status: "На кухне и в жаре",
      shortStatus: "Кухня",
      eta: "24 минуты",
      lead: "Готовим"
    },
    {
      stage: "packing",
      status: "Собираем и упаковываем",
      shortStatus: "Сборка",
      eta: "12 минут",
      lead: "Упаковка"
    },
    {
      stage: "courier",
      status: "Курьер уже в пути",
      shortStatus: "Курьер",
      eta: "6 минут",
      lead: "Доставка"
    },
    {
      stage: "completed",
      status: "Доставлен",
      shortStatus: "Доставлен",
      eta: "Завершен",
      lead: "Архив"
    }
  ];

  const meta = stages[stageIndex];

  return {
    ...order,
    stage: meta.stage,
    status: meta.status,
    shortStatus: meta.shortStatus,
    eta: meta.eta,
    lead: meta.lead,
    isActive: meta.stage !== "completed",
    steps: getDeliverySteps(stageIndex)
  };
}
